import { memo } from 'react';
import { useStore, type StoreApi } from 'zustand';
import { Info, ListChecks, OctagonAlert, RotateCcw, ScanBarcode, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, titleIfClamped } from '@/lib/utils';
import { MrsChip } from '../mrs-hover-card';
import { qty } from '../../utils/picklistProgress';
import { parseQty, type PostDialogState } from './postDialogStore';
import {
  MEMO_MAX_LENGTH,
  mrsLine,
  overBy,
  qtyError,
  qtyTip,
  readingError,
  serialCaption,
  unitWord,
  type PostRow,
  type RowFigures,
} from './postRows';
import { RowTip } from './RowTip';

interface PostLineRowProps {
  row: PostRow;
  store: StoreApi<PostDialogState>;
  figures: RowFigures;
  lookupFailed: boolean;
  loadingReleased: boolean;
  readingSeed: number | null;
  picklistId: number;
  picklistCreatedAt: string;
  onOpenSerials: (row: PostRow) => void;
  onOpenDetails: (row: PostRow, anchor: HTMLElement) => void;
}

/**
 * PT-5 deliverable 2 — one row per planned line (one MRS line). Owns nothing: every edit goes to the dialog's
 * store, and the row subscribes to its own slice, so a keystroke re-renders this row alone (Reading: the rows of
 * this item). The layout (wide or compact) is the lines section's container query; the row renders one markup.
 *
 * The row has no Adjust control (owner 2026-10-05: this dialog is purely for posting). A wrong quantity or serial
 * is corrected from the item's Adjust on the picklist page, with this dialog closed.
 */
export const PostLineRow = memo(function PostLineRow({
  row,
  store,
  figures,
  lookupFailed,
  loadingReleased,
  readingSeed,
  picklistId,
  picklistCreatedAt,
  onOpenSerials,
  onOpenDetails,
}: PostLineRowProps) {
  const checked = useStore(store, (s) => Boolean(s.selected[row.id]));
  const qtyText = useStore(store, (s) => s.qtyText[row.id] ?? '');
  const memoText = useStore(store, (s) => s.memos[row.id] ?? '');
  const readingText = useStore(store, (s) => s.readingText[row.anchorId] ?? '');
  // A string, so the selector's value compares by content: ticking a serial on ANOTHER row re-renders nothing here.
  const tickedKey = useStore(store, (s) =>
    row.serials.filter((u) => u.available && s.ticked[u.slotId]).map((u) => u.serial).join('\u0001'));

  const ticked = tickedKey ? tickedKey.split('\u0001') : [];
  const available = row.serials.filter((u) => u.available).length;
  const q = row.isSerial ? ticked.length : parseQty(qtyText);
  const x = overBy(q, figures);
  const over = x > 0;
  const error = !row.isSerial && qtyError(q, row.max);
  const tip = qtyTip(row, q, figures, available);
  const sub = mrsLine(figures, lookupFailed);
  const readingBad = readingError(readingText);
  const readingChanged = readingText !== (readingSeed == null ? '' : String(readingSeed));
  const nameTitle = `${row.itemCode} · ${row.itemName}`;

  const subLine = loadingReleased ? (
    <Skeleton className="h-3 w-20" />
  ) : (
    <span className={cn('tabular-nums', sub.tone === 'warning' ? 'text-warning' : 'text-muted-foreground')}>{sub.text}</span>
  );

  return (
    <div className="border-b border-border/60 px-[13px] py-[9px]" data-row-id={row.id}>
      <div className="post-row-grid">
        <Checkbox
          className="pa-chk mt-0.5 h-[17px] w-[17px] rounded-[5px]"
          checked={checked}
          onCheckedChange={() => store.getState().toggleRow(row.id)}
          aria-label={`Select ${row.itemCode} · ${row.mrsNumber}`}
        />

        {/* The item cell opens the MRS line details popover: a real control, keyboard reachable. */}
        <div
          role="button"
          tabIndex={0}
          aria-label="View MRS line details"
          onClick={(e) => onOpenDetails(row, e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenDetails(row, e.currentTarget);
            }
          }}
          className="pa-item -mx-1.5 -my-1 min-w-0 cursor-pointer rounded-[7px] px-1.5 py-1 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span
              className="min-w-0 max-w-full truncate font-mono text-[12.5px] font-semibold"
              onPointerEnter={(e) => titleIfClamped(e.currentTarget, nameTitle)}
            >
              {row.itemCode}
            </span>
            <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            {row.splitLabel && (
              <span
                title={row.splitTip ?? undefined}
                className="inline-flex h-[17px] cursor-help items-center whitespace-nowrap rounded-full border border-border px-1.5 text-[10px] font-medium text-muted-foreground"
              >
                {row.splitLabel}
              </span>
            )}
          </div>
          <p
            className="mt-px line-clamp-3 text-[11px] leading-[1.35] text-muted-foreground"
            onPointerEnter={(e) => titleIfClamped(e.currentTarget, nameTitle)}
          >
            {row.itemName}
          </p>
        </div>

        <div className="pa-mrs min-w-0">
          <div className="post-only-compact mb-[5px] h-3.5 truncate text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-muted-foreground">
            For MRS <span className="font-medium normal-case tracking-normal">· {subLine}</span>
          </div>
          <div className="post-mrs-cell">
            <MrsChip
              picklistId={picklistId}
              picklistCreatedAt={picklistCreatedAt}
              materialRequestId={row.materialRequestId}
              mrsNumber={row.mrsNumber}
              requestorFullName={row.requestorFullName}
              departmentName={row.departmentName}
            />
            {/* O-10: Requestor stands alone here; the department shows once, as the Cost center chip below. */}
            <span className="min-w-0 truncate text-[11px] font-medium" title={row.requestorFullName || undefined}>
              {row.requestorFullName || '—'}
            </span>
          </div>
        </div>

        <div className="pa-qty post-qty-cell">
          <div className="post-only-compact mb-[5px] h-3.5 text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-muted-foreground">
            To post
          </div>
          {row.isSerial ? (
            <RowTip tip={tip}>
              <Button
                type="button"
                variant="outline"
                aria-label="Choose serials to post"
                onClick={() => onOpenSerials(row)}
                className={cn(
                  'h-[30px] gap-1.5 whitespace-nowrap rounded-md px-[9px] text-[12.5px] font-semibold tabular-nums',
                  over && 'border-destructive bg-destructive/[0.06] text-destructive',
                )}
              >
                {over && <OctagonAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                {qty(q)} {unitWord(row.unit, q)}
                <ListChecks className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              </Button>
            </RowTip>
          ) : (
            <RowTip tip={tip}>
              <div className="flex items-center gap-[5px]">
                {over && <OctagonAlert className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />}
                <Input
                  type="number"
                  min={0}
                  max={row.max}
                  step="any"
                  value={qtyText}
                  onChange={(e) => store.getState().setQty(row.id, e.target.value)}
                  aria-label="Quantity to post"
                  aria-invalid={error || over}
                  className={cn(
                    'h-[30px] w-[66px] rounded-md px-1.5 text-right text-[12.5px] font-semibold tabular-nums md:text-[12.5px]',
                    (error || over) && 'border-destructive text-destructive',
                    over && 'bg-destructive/[0.06]',
                  )}
                />
                <span className="whitespace-nowrap text-[11.5px] text-muted-foreground">{unitWord(row.unit, q)}</span>
              </div>
            </RowTip>
          )}
          <div className="post-only-wide text-[10.5px]">{subLine}</div>
        </div>

        <div className="pa-rd min-w-0">
          <div className="post-only-compact mb-[5px] h-3.5 text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-muted-foreground">
            Reading
          </div>
          <div className="flex min-w-0 items-center gap-1">
            {/* One reading per ITEM: every row of this item binds to it (C6). */}
            <Input
              type="number"
              value={readingText}
              onChange={(e) => store.getState().setReading(row.anchorId, e.target.value)}
              placeholder="—"
              aria-label="Reading"
              aria-invalid={readingBad}
              className={cn(
                'h-[30px] min-w-0 flex-1 rounded-md px-2 text-right text-xs md:text-xs',
                readingBad && 'border-destructive',
              )}
            />
            {readingChanged && (
              <RowTip tip="Restore the last recorded reading">
                <button
                  type="button"
                  aria-label="Restore the last recorded reading"
                  onClick={() => store.getState().setReading(row.anchorId, readingSeed == null ? '' : String(readingSeed))}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden />
                </button>
              </RowTip>
            )}
          </div>
        </div>

        <div className="pa-memo memo-wrap relative min-w-0">
          <textarea
            value={memoText}
            onChange={(e) => store.getState().setMemo(row.id, e.target.value.slice(0, MEMO_MAX_LENGTH))}
            rows={2}
            maxLength={MEMO_MAX_LENGTH}
            placeholder="Memo (optional)"
            aria-label="Memo for this MIS line"
            className="block min-h-12 w-full resize-y rounded-md border border-input bg-background py-1.5 pl-2 pr-[30px] pb-3.5 text-xs leading-[1.4] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          {memoText && (
            <>
              <RowTip tip="Clear memo">
                <button
                  type="button"
                  aria-label="Clear memo"
                  onClick={() => store.getState().setMemo(row.id, '')}
                  className="memo-clear absolute right-[5px] top-[5px] inline-flex h-5 w-5 items-center justify-center rounded-[5px] bg-muted text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </RowTip>
              <span className="pointer-events-none absolute bottom-[3px] right-2 text-[10px] tabular-nums text-muted-foreground">
                {memoText.length} / {MEMO_MAX_LENGTH}
              </span>
            </>
          )}
        </div>
      </div>

      {(row.departmentName || row.endUseName) && (
        <div className="ml-[34px] mt-[5px] flex min-w-0 flex-wrap items-center gap-1.5">
          {row.departmentName && <MetaChip label="Cost center" value={row.departmentName} />}
          {row.endUseName && <MetaChip label="End use" value={row.endUseName} />}
        </div>
      )}

      {row.isSerial && (
        <div
          className="ml-[34px] mt-1 flex items-center gap-[5px] truncate font-mono text-[10.5px] font-medium text-muted-foreground"
          title={ticked.join(', ') || undefined}
        >
          <ScanBarcode className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{serialCaption(ticked)}</span>
        </div>
      )}
    </div>
  );
});

function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <span
      className="inline-flex h-[22px] max-w-full items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md bg-muted/50 px-2 text-[11px]"
      title={`${label}: ${value}`}
    >
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-foreground">{value}</span>
    </span>
  );
}
